import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { WEEKDAY_OPTIONS } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, School, CalendarDays, Image as ImageIcon } from "lucide-react";

const fileToDataURL = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: "", activity_day: 1, day_label: "Selasa", active: true });

  const load = () => api.get("/schools").then((r) => setSchools(r.data));
  useEffect(() => { load(); }, []);

  const uploadLogo = async (school, file) => {
    if (!file) return;
    if (file.size > 2_000_000) { toast.error("Ukuran logo maksimal 2MB"); return; }
    try { const data_url = await fileToDataURL(file); await api.put(`/schools/${school.id}/logo`, { data_url }); toast.success("Logo sekolah diperbarui"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal unggah logo"); }
  };

  const openNew = () => { setForm({ name: "", activity_day: 1, day_label: "Selasa", active: true }); setDialog("new"); };
  const openEdit = (s) => { setForm({ ...s }); setDialog(s.id); };

  const setDay = (v) => {
    const opt = WEEKDAY_OPTIONS.find((o) => o.value === Number(v));
    setForm({ ...form, activity_day: Number(v), day_label: opt.label });
  };

  const save = async () => {
    try {
      if (dialog === "new") await api.post("/schools", form);
      else await api.put(`/schools/${dialog}`, form);
      toast.success("Data sekolah tersimpan");
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-5" data-testid="schools-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Data Sekolah</h2>
          <p className="text-sm text-muted-foreground">Sekolah dan hari kegiatan</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-school-btn">
          <Plus className="w-4 h-4" /> Tambah
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {schools.map((s) => (
          <Card key={s.id} className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] p-5 bg-white" data-testid={`school-card-${s.id}`}>
            <div className="flex items-center gap-3 mb-3">
              {s.logo_url ? (
                <img src={s.logo_url} alt={s.name} className="w-12 h-12 rounded-2xl object-contain border border-slate-100 bg-white" data-testid={`school-logo-${s.id}`} />
              ) : (
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-gtsorange to-gtsorange-light flex items-center justify-center"><School className="w-6 h-6 text-white" /></div>
              )}
            </div>
            <p className="font-nunito font-bold text-gtsnavy">{s.name}</p>
            <Badge className="mt-2 rounded-full bg-gtsnavy/10 text-gtsnavy gap-1"><CalendarDays className="w-3 h-3" /> Setiap {s.day_label}</Badge>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => openEdit(s)} className="rounded-full flex-1 gap-1" data-testid={`edit-school-${s.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</Button>
              <label>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(s, e.target.files?.[0])} data-testid={`school-logo-input-${s.id}`} />
                <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`school-logo-btn-${s.id}`}><ImageIcon className="w-3.5 h-3.5" /> Logo</span>
              </label>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{dialog === "new" ? "Tambah Sekolah" : "Edit Sekolah"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Sekolah</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="school-name-input" />
            </div>
            <div>
              <Label>Hari Kegiatan</Label>
              <Select value={String(form.activity_day)} onValueChange={setDay}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="school-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAY_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-school-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
